import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TradeEntry } from '../../models/trade-entry';
import { ApiService, SetupPerformance } from '../../services/api.service';
import { Subject, firstValueFrom, takeUntil } from 'rxjs';
import { RiskBudgetService } from '../../services/risk-budget.service';

@Component({
  selector: 'app-stats',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './stats.component.html',
  styleUrls: ['./stats.component.scss']
})
export class StatsComponent implements OnInit, OnDestroy {
  private readonly defaultSetupNames = ['Ausbruch', 'Seitwert', 'Trendfolge'];
  trades: TradeEntry[] = [];
  setupPerformance: SetupPerformance[] = this.emptySetupPerformance();
  setupPerformanceMessage = '';
  private readonly destroy$ = new Subject<void>();

  constructor(private api: ApiService, private riskBudget: RiskBudgetService) {}

  ngOnInit(): void {
    this.refresh();
    this.api.tradeChanges$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.refresh();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  refresh(): void {
    this.loadFromApi();
    this.loadSetupPerformance();
  }

  observationCount(): number {
    return this.trades.filter((t) => t.status === 'Watch').length;
  }

  observationVolume(): number {
    const volume = this.trades
      .filter((t) => t.status === 'Watch')
      .reduce((sum, entry) => sum + this.positionVolume(entry), 0);

    return +volume.toFixed(2);
  }

  openPositionCount(): number {
    return this.trades.filter((t) => t.status === 'Positioniert' || t.status === 'Teilverkauf').length;
  }

  openPositionVolume(): number {
    const volume = this.trades
      .filter((t) => t.status === 'Positioniert' || t.status === 'Teilverkauf')
      .reduce((sum, entry) => sum + this.positionVolume(entry), 0);

    return +volume.toFixed(2);
  }

  closedPositionCount(): number {
    return this.trades.filter((t) => t.status === 'Glatt').length;
  }

  totalTradesCount(): number {
    return this.trades.filter((t) => t.status === 'Positioniert' || t.status === 'Glatt').length;
  }

  averageCrv(): number {
    const crvs = this.trades
      .map((entry) => this.getRiskReward(entry))
      .filter((value) => value > 0);

    if (!crvs.length) return 0;
    const total = crvs.reduce((sum, value) => sum + value, 0);
    return +(total / crvs.length).toFixed(2);
  }

  averageHoldingDays(): number {
    const durations = this.trades
      .filter((entry) => entry.status === 'Glatt' && entry.entryDate && entry.sellDate)
      .map((entry) => {
        const entryParts = this.parseDateParts(entry.entryDate as string);
        const sellParts = this.parseDateParts(entry.sellDate as string);
        if (!entryParts || !sellParts) return 0;
        const days = sellParts.day - entryParts.day;
        return Number.isFinite(days) ? Math.max(days, 0) : 0;
      })
      .filter((days) => days > 0);

    if (!durations.length) return 0;
    const total = durations.reduce((sum, days) => sum + days, 0);
    return +(total / durations.length).toFixed(1);
  }

  totalPnl(): number {
    return this.trades
      .filter((entry) => entry.status === 'Glatt' || (entry.partialSales?.length ?? 0) > 0)
      .reduce((sum, entry) => sum + this.realizedPnl(entry), 0);
  }

  totalR(): number {
    return +this.realizedTrades()
      .reduce((sum, entry) => sum + this.realizedR(entry), 0)
      .toFixed(2);
  }

  averageR(): number {
    const trades = this.realizedTrades();
    if (!trades.length) return 0;
    return +(this.totalR() / trades.length).toFixed(2);
  }

  profitFactor(): number {
    const wins = this.realizedTrades()
      .map((entry) => this.realizedPnl(entry))
      .filter((value) => value > 0)
      .reduce((sum, value) => sum + value, 0);
    const losses = Math.abs(this.realizedTrades()
      .map((entry) => this.realizedPnl(entry))
      .filter((value) => value < 0)
      .reduce((sum, value) => sum + value, 0));
    if (losses === 0) return wins > 0 ? wins : 0;
    return +(wins / losses).toFixed(2);
  }

  maxDrawdown(): number {
    let high = 0;
    let equity = 0;
    let maxDrawdown = 0;
    this.realizedTradesChronological().forEach((entry) => {
      equity += this.realizedPnl(entry);
      high = Math.max(high, equity);
      maxDrawdown = Math.max(maxDrawdown, high - equity);
    });
    return +maxDrawdown.toFixed(2);
  }

  currentDrawdown(): number {
    let high = 0;
    let equity = 0;
    this.realizedTradesChronological().forEach((entry) => {
      equity += this.realizedPnl(entry);
      high = Math.max(high, equity);
    });
    return +(high - equity).toFixed(2);
  }

  openReviewCount(): number {
    return this.trades.filter((entry) => entry.reviewRequired && !entry.reviewCompleted).length;
  }

  ruleBreakCount(): number {
    return this.realizedTrades().filter((entry) => entry.ruleBroken === true || !!entry.mistakeCategories).length;
  }

  agentTradeCount(): number {
    return this.realizedTrades().filter((entry) => !!entry.analysisId || !!entry.selectedVariantName).length;
  }

  agentPositiveRate(): number {
    const trades = this.realizedTrades().filter((entry) => !!entry.analysisId || !!entry.selectedVariantName);
    if (!trades.length) return 0;
    const positives = trades.filter((entry) => this.realizedPnl(entry) > 0).length;
    return +((positives / trades.length) * 100).toFixed(1);
  }

  improvementHints(): string[] {
    const hints: string[] = [];
    if (this.openReviewCount() > 0) {
      hints.push(`${this.openReviewCount()} abgeschlossene Trades haben noch kein Review.`);
    }
    if (this.ruleBreakCount() > 0) {
      hints.push(`${this.ruleBreakCount()} realisierte Trades enthalten Regelbruch- oder Fehlerhinweise.`);
    }
    if (this.realizedTrades().length >= 3 && this.averageR() < 0) {
      hints.push(`Der durchschnittliche R-Wert ist negativ (${this.averageR()}R). Prioritaet: Setup-Qualitaet und Risiko pruefen.`);
    }
    const shortTrades = this.realizedTrades().filter((entry) => entry.direction === 'Short');
    if (shortTrades.length >= 3) {
      const shortAvg = shortTrades.reduce((sum, entry) => sum + this.realizedR(entry), 0) / shortTrades.length;
      if (shortAvg < 0) {
        hints.push(`Short-Trades haben aktuell negative Erwartung (${shortAvg.toFixed(2)}R).`);
      }
    }
    const noScreenshot = this.realizedTrades().filter((entry) => !(entry.screenshots?.length));
    if (noScreenshot.length >= 3) {
      hints.push(`${noScreenshot.length} realisierte Trades haben keinen Screenshot. Pruefe, ob diese schlechter abschneiden.`);
    }
    const withoutPlaybook = this.realizedTrades().filter((entry) => !entry.playbookName);
    if (withoutPlaybook.length >= 3) {
      hints.push(`${withoutPlaybook.length} realisierte Trades sind keinem Playbook zugeordnet.`);
    }
    const unfocusedDays = this.realizedTrades().filter((entry) => !entry.dailyFocus);
    if (unfocusedDays.length >= 3) {
      hints.push(`${unfocusedDays.length} realisierte Trades haben keinen Tagesfokus im Tagesjournal.`);
    }
    return hints;
  }

  winningTradesCount(): number {
    return this.realizedTrades().filter((entry) => this.realizedPnl(entry) > 0).length;
  }

  losingTradesCount(): number {
    return this.realizedTrades().filter((entry) => this.realizedPnl(entry) < 0).length;
  }

  lossWinRatioPct(): number {
    const total = this.winningTradesCount() + this.losingTradesCount();
    if (!total) return 0;
    return +((this.winningTradesCount() / total) * 100).toFixed(1);
  }

  profitSeries(): { x: number; y: number; label: string }[] {
    const points = this.trades
      .filter((entry) => entry.status === 'Glatt' || (entry.partialSales?.length ?? 0) > 0)
      .map((entry) => {
        const dateStr = entry.sellDate || entry.entryDate || '';
        const date = dateStr ? new Date(dateStr) : new Date();
        return { date, pnl: this.realizedPnl(entry) };
      })
      .sort((a, b) => a.date.getTime() - b.date.getTime());

    let cumulative = 0;
    return points.map((p, index) => {
      cumulative += p.pnl;
      return {
        x: index,
        y: cumulative,
        label: p.date.toISOString().substring(0, 10),
      };
    });
  }

  chartViewBox(): string {
    return '0 0 1000 360';
  }

  chartPath(): string {
    const points = this.profitSeries();
    if (!points.length) return '';

    return points
      .map((p, idx) => `${idx === 0 ? 'M' : 'L'} ${this.chartX(p.x).toFixed(2)} ${this.chartY(p.y).toFixed(2)}`)
      .join(' ');
  }

  chartAreaPath(): string {
    const path = this.chartPath();
    const points = this.profitSeries();
    if (!path || !points.length) return '';
    const baseY = this.chartY(Math.max(0, this.chartMinY()));
    const firstX = this.chartX(points[0].x);
    const lastX = this.chartX(points[points.length - 1].x);
    return `${path} L ${lastX.toFixed(2)} ${baseY.toFixed(2)} L ${firstX.toFixed(2)} ${baseY.toFixed(2)} Z`;
  }

  yAxisTicks(): number[] {
    const minY = this.chartMinY();
    const maxY = this.chartMaxY();
    const step = (maxY - minY) / 4 || 1;
    return Array.from({ length: 5 }, (_, i) => +(minY + step * i).toFixed(2));
  }

  xAxisLabels(): { x: number; label: string }[] {
    const points = this.profitSeries();
    if (!points.length) return [];
    const desired = Math.min(5, points.length);
    if (desired <= 1) {
      return [{ x: this.chartX(0), label: this.formatDateLabel(points[0].label) }];
    }
    const maxIndex = points.length - 1;
    const selected = Array.from({ length: desired }, (_, i) => Math.round((i / (desired - 1)) * maxIndex));
    return [...new Set(selected)].map((index) => ({
      x: this.chartX(points[index].x),
      label: this.formatDateLabel(points[index].label)
    }));
  }

  chartX(index: number): number {
    const points = this.profitSeries();
    const maxX = Math.max(points.length - 1, 1);
    return 72 + (index / maxX) * 888;
  }

  chartY(value: number): number {
    const minY = this.chartMinY();
    const maxY = this.chartMaxY();
    const span = maxY - minY || 1;
    return 284 - ((value - minY) / span) * 226;
  }

  chartZeroY(): number {
    return this.chartY(0);
  }

  chartMinY(): number {
    const values = this.profitSeries().map((p) => p.y);
    const min = Math.min(0, ...values);
    const max = Math.max(0, ...values);
    const padding = Math.max((max - min) * 0.12, 100);
    return Math.floor((min - padding) / 100) * 100;
  }

  chartMaxY(): number {
    const values = this.profitSeries().map((p) => p.y);
    const min = Math.min(0, ...values);
    const max = Math.max(0, ...values);
    const padding = Math.max((max - min) * 0.12, 100);
    return Math.ceil((max + padding) / 100) * 100;
  }

  formatEuroTick(value: number): string {
    return `${value.toLocaleString('de-DE', { maximumFractionDigits: 0 })} €`;
  }

  private formatDateLabel(value: string): string {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return `${String(date.getDate()).padStart(2, '0')}.${String(date.getMonth() + 1).padStart(2, '0')}.${date.getFullYear()}`;
  }

  setupPerformanceTrack(_: number, item: SetupPerformance): string {
    return item.setup;
  }

  private realizedPnl(entry: TradeEntry): number {
    if (entry.partialSales?.length) {
      return entry.partialSales.reduce((sum, sale) => sum + sale.pnl, 0);
    }

    const size = entry.sellSize ?? entry.size;
    const sellPrice = entry.sellPrice;
    const entryFee = entry.entryFee ?? 0;
    const sellFee = entry.sellFee ?? 0;
    if (sellPrice === undefined || size <= 0) return entry.realizedPnl ?? 0;

    const perShare = (entry.certificate ? 'Long' : entry.direction) === 'Long'
      ? sellPrice - entry.entryPrice
      : entry.entryPrice - sellPrice;

    return +((perShare * size) - entryFee - sellFee).toFixed(2);
  }

  private realizedTrades(): TradeEntry[] {
    return this.trades.filter((entry) => entry.status === 'Glatt' || (entry.partialSales?.length ?? 0) > 0);
  }

  private realizedTradesChronological(): TradeEntry[] {
    return [...this.realizedTrades()].sort((a, b) => {
      const aDate = new Date(a.sellDate || a.entryDate || '').getTime() || 0;
      const bDate = new Date(b.sellDate || b.entryDate || '').getTime() || 0;
      return aDate - bDate;
    });
  }

  private initialRiskTotal(entry: TradeEntry): number {
    const stored = Number(entry.initialRiskTotalEUR ?? 0);
    if (stored > 0) return stored;
    const stop = entry.initialStopPrice ?? entry.stopPricePrevious ?? entry.stopPrice;
    return Math.abs((entry.entryPrice ?? 0) - (stop ?? 0)) * (entry.size ?? 0) + (entry.entryFee ?? 0);
  }

  private realizedR(entry: TradeEntry): number {
    const stored = Number(entry.realizedR ?? 0);
    if (stored !== 0) return stored;
    const risk = this.initialRiskTotal(entry);
    if (risk <= 0) return 0;
    return +(this.realizedPnl(entry) / risk).toFixed(2);
  }

  private getRiskReward(entry: TradeEntry): number {
    const stopForCrv = entry.stopPricePrevious ?? entry.stopPrice;
    const risk = Math.abs(entry.entryPrice - stopForCrv);
    const reward = (entry.certificate ? 'Long' : entry.direction) === 'Long'
      ? entry.targetPrice - entry.entryPrice
      : entry.entryPrice - entry.targetPrice;
    if (risk <= 0 || reward <= 0) return 0;
    return +(reward / risk).toFixed(2);
  }

  private parseDateParts(value: string): { year: number; month: number; day: number } | null {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    if (!match) return null;
    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) {
      return null;
    }
    return { year, month, day };
  }

  private async loadFromApi(): Promise<void> {
    try {
      const remote = await firstValueFrom(this.api.getTrades());
      this.trades = (remote ?? []).map((entry) => this.normalizeEntry(entry));
      this.updatePositionedPnl();
    } catch {
      this.trades = [];
      this.updatePositionedPnl();
    }
  }

  private async loadSetupPerformance(): Promise<void> {
    this.setupPerformanceMessage = '';
    try {
      const response = await firstValueFrom(this.api.getSetupPerformance());
      const items = response?.success ? response.items ?? [] : [];
      this.setupPerformance = this.mergeDefaultSetupPerformance(items);
      if (!response?.success) {
        this.setupPerformanceMessage = response?.message || 'Setup-Auswertung konnte nicht geladen werden.';
      }
    } catch {
      this.setupPerformance = this.emptySetupPerformance();
      this.setupPerformanceMessage = 'Setup-Auswertung konnte nicht vom Backend geladen werden.';
    }
  }

  private emptySetupPerformance(): SetupPerformance[] {
    return this.defaultSetupNames.map((setup) => ({
      setup,
      totalResults: 0,
      positiveResults: 0,
      negativeResults: 0,
      neutralResults: 0,
      successRatePct: 0,
      totalPnl: 0,
    }));
  }

  private mergeDefaultSetupPerformance(items: SetupPerformance[]): SetupPerformance[] {
    const bySetup = new Map<string, SetupPerformance>();

    this.emptySetupPerformance().forEach((item) => bySetup.set(item.setup.toLowerCase(), item));
    items.forEach((item) => bySetup.set(item.setup.toLowerCase(), item));

    return [
      ...this.defaultSetupNames
        .map((setup) => bySetup.get(setup.toLowerCase()))
        .filter((item): item is SetupPerformance => !!item),
      ...items.filter((item) => !this.defaultSetupNames.some((setup) => setup.toLowerCase() === item.setup.toLowerCase())),
    ];
  }

  private updatePositionedPnl(): void {
    const pnl = this.trades
      .filter((t) => t.status !== 'Watch')
      .reduce((sum, entry) => sum + this.realizedPnl(entry), 0);
    this.riskBudget.updatePositionedPnl(+pnl.toFixed(2));
  }

  private calculateRealizedPnl(entry: TradeEntry, sellPrice: number, sellSize: number, sellFee = 0): number {
    if (sellPrice === undefined || sellSize <= 0) {
      return entry.realizedPnl ?? 0;
    }

    const perShare = (entry.certificate ? 'Long' : entry.direction) === 'Long'
      ? sellPrice - entry.entryPrice
      : entry.entryPrice - sellPrice;

    const proportionalEntryFee = (entry.entryFee ?? 0) * (sellSize / (entry.size || sellSize || 1));
    const total = perShare * sellSize - proportionalEntryFee - (sellFee ?? 0);
    return +total.toFixed(2);
  }

  private positionVolume(entry: TradeEntry): number {
    return (entry.entryPrice ?? 0) * (entry.size ?? 0);
  }

  private normalizeEntry(entry: TradeEntry): TradeEntry {
    const partialSales = (entry.partialSales ?? []).map((sale) => ({
      ...sale,
      pnl: this.calculateRealizedPnl(entry, sale.price, sale.size, sale.fee ?? 0),
    }));
    const partialPnl = partialSales.reduce((sum, sale) => sum + sale.pnl, 0);
    const status: TradeEntry['status'] = entry.status ?? (entry.positioned ? 'Positioniert' : 'Watch');
    return {
      ...entry,
      status,
      partialSales,
      realizedPnl: partialSales.length ? partialPnl : entry.realizedPnl ?? 0,
      positioned: status !== 'Watch' && status !== 'Glatt' ? true : status === 'Glatt' ? false : entry.positioned,
      sellDate: entry.sellDate ?? '',
      sellPrice: entry.sellPrice,
      sellSize: entry.sellSize ?? entry.size,
    };
  }
}
